import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import en from './en.json';
import fr from './fr.json';

export type Lang = 'en' | 'fr';
const DICTS = { en, fr };
type Dict = typeof en;

type LangCtx = {
  lang: Lang;
  otherLang: Lang;
  t: (path: string) => string;
  toggle: () => void;
  setLang: (l: Lang) => void;
};

const Ctx = createContext<LangCtx | null>(null);

function get(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, k) => {
    if (acc && typeof acc === 'object' && k in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[k];
    }
    return undefined;
  }, obj);
}

// NOTE: artifacts can't use localStorage, but this is a real deployed app (not an artifact),
// so localStorage is fine here for persisting language choice.
function readStored(): Lang {
  try {
    const v = localStorage.getItem('tc26-lang');
    return v === 'fr' ? 'fr' : 'en';
  } catch {
    return 'en';
  }
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>('en');

  useEffect(() => { setLangState(readStored()); }, []);

  const setLang = (l: Lang) => {
    setLangState(l);
    try { localStorage.setItem('tc26-lang', l); } catch { /* ignore */ }
    document.documentElement.lang = l;
  };

  const value = useMemo<LangCtx>(() => {
    const dict = DICTS[lang] as Dict;
    return {
      lang,
      otherLang: lang === 'en' ? 'fr' : 'en',
      t: (path: string) => {
        const v = get(dict, path);
        return typeof v === 'string' ? v : path;
      },
      toggle: () => setLang(lang === 'en' ? 'fr' : 'en'),
      setLang
    };
  }, [lang]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useT() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useT must be used within LangProvider');
  return ctx;
}
